// React component to display LocaionInfo

import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getGeoLink, getMapsSearchUrl } from '../../../../utils/mapUtils';
import TwBadge from '../../../TwBadge/TwBadge';
import { LoadLocation } from '../../../../redux/api/load/load.model';
import useFeatureFlags from '../../../../providers/FeatureFlags/FeatureFlags.provider';
import { TwIconOnlyButton } from '../../../TwButtons/TwButtons';
import { ReactComponent as SignatureIcon } from '../../../../assets/icons/signature-icon.svg';
import { StopStatus, Trip } from '../../../../redux/api/trips/trips.model';
import { isSafariOnIOSMobile, isWebInFlutter } from '../../../../utils/browserUtils';
import TwLoadTooltipInfo, {
  TwLoadTooltipInfoProps,
  TwTripTooltipInfoProps
} from '../../../TwLoadTooltipInfo/TwLoadTooltipInfo';
import TwLoadTooltipContent from '../../../TwLoadTooltipInfo/TwLoadTooltipContent';
import { setOrderId } from '../../../../redux/features/orders/ordersSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  closeLoadDetail,
  closeTripDetail,
  openLoadDetail,
  openTripDetail
} from '../../../../redux/features/navigation/navigationSlice';
import TwTripTooltipContent from '../../../TwLoadTooltipInfo/TwTripTooltipContent';
import { updateTripId } from '../../../../redux/features/trip/tripSlice';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { useGetTenantRoutingSettingsQuery } from '../../../../redux/api/tenant/tenantApi';

export interface LocationInfoProps {
  /**
   * location
   * */
  location?: LoadLocation | null;
  /**
   * boolean to show if the link is for customer or not
   */
  isCustomerLink: boolean;
  /**
   * stop status
   */
  status?: StopStatus;
  /**
   * Next stop status
   */
  nextStopStatus?: StopStatus;
  /**
   * Boolean to show/hide the signature icon button
   */
  hasSignature?: boolean;
  /**
   * Function to view signature
   */
  onViewSignature?: () => void;
  /**
   * Related LTL Order readable id
   */
  LTLOrderReadableId?: string | number;
  /**
   * Related LTL Trip readable id
   */
  LTLTripReadableId?: string | number;
  /**
   * Orders tooltip data
   */
  ordersTooltipDataArray?: TwLoadTooltipInfoProps[];
  /**
   * Trips tooltip data
   */
  tripsTooltipDataArray?: TwTripTooltipInfoProps[];
}

const LocationInfo: React.FC<LocationInfoProps> = ({
  location,
  status,
  nextStopStatus,
  isCustomerLink,
  hasSignature,
  LTLOrderReadableId,
  LTLTripReadableId,
  ordersTooltipDataArray,
  tripsTooltipDataArray,
  onViewSignature
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { enableAddressLocationLink, enableAndroidCreateGeoLink } = useFeatureFlags();
  const isLoadDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const isTripDetailOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);
  const dispatch = useAppDispatch();

  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);

  if ((!location || (!location?.name && !location?.full_address)) && !hasSignature) return <></>;

  const subLinkStyle = { color: '#6B7280', textDecoration: 'none' };

  const handleViewSignature = (): void => {
    onViewSignature && onViewSignature();
  };

  const shouldGenerateAndroidGeoLink =
    enableAndroidCreateGeoLink && isWebInFlutter() && !isSafariOnIOSMobile();

  const hasGoogleLinksEnabled = !routingSettings || routingSettings?.config?.google_links;

  const locationUrl = !hasGoogleLinksEnabled
    ? undefined
    : location
    ? shouldGenerateAndroidGeoLink
      ? getGeoLink(location, enableAddressLocationLink)
      : getMapsSearchUrl(location, enableAddressLocationLink)
    : undefined;

  // Gets order tooltip data
  const getTooltipDataByOrderReadableId = (
    orderReadableId: string | number
  ): TwLoadTooltipInfoProps | undefined => {
    return ordersTooltipDataArray?.find(
      (tooltipData) => tooltipData.loadReadableId.toString() === orderReadableId.toString()
    );
  };

  // Gets trip tooltip data
  const getTooltipDataByTripReadableId = (
    tripReadableId: string | number
  ): TwTripTooltipInfoProps | undefined => {
    return tripsTooltipDataArray?.find(
      (tooltipData) => tooltipData.tripReadableId?.toString() === tripReadableId.toString()
    );
  };

  const handleOverlayOpen = (): void => {
    if (LTLTripReadableId) {
      dispatch(closeLoadDetail());
      dispatch(closeTripDetail());
      setTimeout(() => dispatch(openTripDetail()), 500);
    } else if (LTLOrderReadableId) {
      dispatch(closeTripDetail());
      dispatch(closeLoadDetail());
      setTimeout(() => dispatch(openLoadDetail()), 500);
    }
  };

  const showOrderTooltip = LTLOrderReadableId && enableLtl;
  const showTripTooltip = LTLTripReadableId && enableLtl;

  return (
    <Stack
      direction="column"
      spacing={0}
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{ width: '100%' }}
    >
      <Stack direction="row" spacing={1} alignItems={'flex-end'}>
        <a href={locationUrl} rel="noreferrer" target="_blank" style={{ color: '#1F2937' }}>
          <Typography variant="subtitle2" fontWeight="600" color="#1F2937" fontSize="14px">
            {location?.name}
          </Typography>
        </a>
        {location && location.is_appointment_required && (
          <TwBadge
            content="Appt."
            sx={{
              borderRadius: '12px !important',
              color: '#4B5563 !important',
              backgroundColor: '#F3F4F6 !important'
            }}
          />
        )}
        {status && isCustomerLink && (
          <TwBadge
            content={
              status === StopStatus.inTransit
                ? 'In Transit'
                : status === StopStatus.arrived
                ? 'Arrived'
                : status === StopStatus.complete && nextStopStatus === StopStatus.notStarted
                ? 'Complete'
                : ''
            }
            sx={{
              borderRadius: '12px !important',
              color: '#3a4de9 !important',
              backgroundColor: '#ebedfd !important'
            }}
          />
        )}
        {hasSignature && (
          <TwIconOnlyButton
            className="add-signature__view-signature-button"
            ariaLabel={'View signature'}
            icon={<SignatureIcon />}
            action={handleViewSignature}
          />
        )}
      </Stack>
      <Stack direction="row">
        <Typography variant="body1" fontWeight="400" color="#6B7280" fontSize="14px">
          <a
            className="load-detail__data-container__data__route__info__location-link"
            rel="noreferrer"
            target="_blank"
            href={locationUrl}
            style={subLinkStyle}
          >
            {location?.full_address}
          </a>
          {/* Show Order tooltip in Trip overlay route */}
          {showOrderTooltip && ' • '}
          {showOrderTooltip && (
            <TwLoadTooltipInfo
              tooltipContent={
                <TwLoadTooltipContent data={getTooltipDataByOrderReadableId(LTLOrderReadableId)} />
              }
            >
              <span
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(): void => {
                  const load = getTooltipDataByOrderReadableId(LTLOrderReadableId);
                  if (!load) return;

                  dispatch(setOrderId(load.loadId));
                  handleOverlayOpen();
                }}
              >
                Order #{LTLOrderReadableId}
              </span>
            </TwLoadTooltipInfo>
          )}
          {/* Show Trip tooltip in Order overlay route */}
          {showTripTooltip && ' • '}
          {showTripTooltip && (
            <TwLoadTooltipInfo
              tooltipContent={
                <TwTripTooltipContent
                  tripData={getTooltipDataByTripReadableId(LTLTripReadableId)}
                />
              }
            >
              <span
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={(): void => {
                  const trip = getTooltipDataByTripReadableId(LTLTripReadableId);
                  if (!trip) return;

                  dispatch(updateTripId(trip.tripId));
                  handleOverlayOpen();
                }}
              >
                Trip #{LTLTripReadableId}
              </span>
            </TwLoadTooltipInfo>
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};
export default LocationInfo;
